import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { defaultBlightDayRange } from '@next/arable-lib/shared';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { distinctUntilChanged, filter } from 'rxjs';

import {
  mapDownyMildewDataToCropFieldRegistration,
  mapDownyMildewDaysToChartDays,
} from 'src/app/downy-mildew/models/mappers';
import { DiseaseChartDay } from 'src/app/shared/components/disease-insight-chart/models';
import { CHART_CONFIG } from 'src/app/shared/components/disease-insight-chart/base-chart.config';
import { DownyMildewChartConfig } from 'src/app/downy-mildew/shared/downy-mildew-chart.config';
import { DownyMildewSignalStore } from 'src/app/downy-mildew/signal-store/downy-mildew-signal-store';
import { DateService } from 'src/app/shared/services/date.service';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import { RecalculateButtonComponent } from 'src/app/shared/components/recalculate-button/recalculate-button.component';
import { DateSelectorComponent } from 'src/app/shared/components/date-selector.component/date-selector.component';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import {
  type DateFieldConfig,
  DiseaseInsightToolbarComponent,
} from 'src/app/shared/components/disease-insight-toolbar.component/disease-insight-toolbar.component';
import { DownyMildewActionMenuComponent } from 'src/app/downy-mildew/shared/components/action-menu.component';
import { MatOption } from '@angular/material/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { DiseaseInsightChartComponent } from 'src/app/shared/components/disease-insight-chart/disease-insight-chart.component';
import {
  DownyMildewCropFieldRegistration,
  DownyMildewRiskLevel,
  DownyMildewRisks,
} from 'src/app/downy-mildew/models/models';
import { getRiskLevelStyling, isAdviceHidden } from 'src/app/downy-mildew/shared/helpers/advice-style.helper';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';
import { createDownyMildewTelemetryProperties, DownyMildewAIIdentifiers } from '../../../../shared/constants';
import { GrowthStageRegistration } from 'src/app/downy-mildew/models/growth-stage.models';
import { longestCropFieldText } from 'src/app/shared/utils/panel-min-width.utils';

@Component({
  selector: 'app-downy-mildew-insight-page',
  templateUrl: './downy-mildew-insight-page.component.html',
  styleUrl: './downy-mildew-insight-page.component.scss',
  providers: [{ provide: CHART_CONFIG, useClass: DownyMildewChartConfig }],
  imports: [
    RecalculateButtonComponent,
    DateSelectorComponent,
    TranslatePipe,
    ReactiveFormsModule,
    DiseaseInsightToolbarComponent,
    DownyMildewActionMenuComponent,
    MatSelect,
    MatOption,
    MatButton,
    MatMenuTrigger,
    MatIcon,
    DiseaseInsightChartComponent,
  ],
})
export class DownyMildewInsightPageComponent {
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(DownyMildewSignalStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dateService = inject(DateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly localeService = inject(LocaleService);
  private readonly ai = inject(AppInsightsServiceInjector);

  disableCropFieldSelector = input(false);

  protected readonly cropFields = linkedSignal<
    ReturnType<typeof this.store.downyMildewAdvices> | undefined,
    ReturnType<typeof this.store.downyMildewAdvices>
  >({
    source: () => (this.store.downyMildewDataIsLoading() ? undefined : this.store.downyMildewAdvices()),
    computation: (source, previous) => source ?? previous?.value ?? [],
  });

  protected readonly longestOptionText = computed(() => longestCropFieldText(this.cropFields()));

  protected readonly minimalDate = this.store.downyMildewMinAdviceDate;
  protected readonly selectedCropFieldAdvice = this.store.selectedCropFieldAdvice;
  protected readonly hasAdvices = this.store.hasSelectedCropFieldAdvices;

  protected readonly selectedDates = computed((): Date[] =>
    this.store.fromDate() || this.store.toDate() ? [this.store.fromDate(), this.store.toDate()] : [],
  );

  protected readonly maximalDate = computed(() =>
    this.dateService.daysInTheFuture(defaultBlightDayRange.daysInFuture, this.store.cropYear()),
  );
  protected readonly isReadOnly = computed(() => !this.selectedCropField() || this.isCropYearReadOnly());
  protected readonly plantDateFieldConfig = computed<DateFieldConfig>(() => ({
    labelKey: 'downyMildew.details.sowingDate',
    mode: this.isReadOnly() ? 'disabled' : 'editable',
    disabledReasonKey: this.isReadOnly() ? 'UI.CurrentYearOnlyMutationMessage' : undefined,
  }));
  protected readonly emergenceDateFieldConfig = computed<DateFieldConfig>(() => ({
    labelKey: 'downyMildew.details.thirdLeafStageDate',
    value: this.selectedCropField()?.thirdLeafStageDate ?? null,
    mode: this.isReadOnly() ? 'disabled' : 'readonly-action',
    disabledReasonKey: this.isReadOnly() ? 'UI.CurrentYearOnlyMutationMessage' : undefined,
  }));
  protected readonly harvestDateFieldConfig = computed<DateFieldConfig>(() => ({
    labelKey: 'downyMildew.details.leavesBentOverDate',
    value: this.selectedCropField()?.leavesBentOverDate ?? null,
    mode: this.isReadOnly() ? 'disabled' : 'readonly-action',
    disabledReasonKey: this.isReadOnly() ? 'UI.CurrentYearOnlyMutationMessage' : undefined,
  }));
  protected readonly isRecalculationDisabled = linkedSignal<boolean | undefined, boolean>({
    source: () => {
      if (this.store.downyMildewDataIsLoading()) return undefined;
      return (
        this.store.isDownyMildewCropFieldsSyncing() ||
        this.isReadOnly() ||
        this.store.isSelectedCropFieldIneligibleForRecalculation()
      );
    },
    computation: (source, previous) => source ?? previous?.value ?? true,
  });
  protected readonly selectedCropField = linkedSignal<
    DownyMildewCropFieldRegistration | undefined,
    DownyMildewCropFieldRegistration | undefined
  >({
    source: () => {
      if (this.store.downyMildewDataIsLoading()) return undefined;
      const data = this.store.selectedDownyMildewData();
      if (!data) {
        return undefined;
      }

      const growthStageHistory = this.store.growthStageHistoryValue();
      const baseRegistration = mapDownyMildewDataToCropFieldRegistration(data);
      const thirdLeafStageDate = getLatestGrowthStageDate(growthStageHistory, data.cropFieldId, thirdLeafStageBbchCode);
      const leavesBentOverDate = getLatestGrowthStageDateByCodes(growthStageHistory, data.cropFieldId, [
        leavesBentOverBbchCode,
        leavesBentOverLegacyBbchCode,
      ]);

      return {
        ...baseRegistration,
        thirdLeafStageDate,
        leavesBentOverDate,
      };
    },
    computation: (source, previous) => {
      if (!source) {
        return previous?.value;
      }

      const previousValue = previous?.value;
      if (previousValue?.cropFieldId !== source.cropFieldId) {
        return source;
      }

      if (this.store.growthStageHistoryIsLoading()) {
        return {
          ...source,
          thirdLeafStageDate: source.thirdLeafStageDate ?? previousValue.thirdLeafStageDate,
          leavesBentOverDate: source.leavesBentOverDate ?? previousValue.leavesBentOverDate,
        };
      }

      return source;
    },
  });

  protected readonly chartDays = linkedSignal<DiseaseChartDay[] | undefined, DiseaseChartDay[]>({
    source: () => {
      if (this.store.downyMildewDataIsLoading()) return undefined;
      const data = this.store.selectedDownyMildewData();
      return data ? mapDownyMildewDaysToChartDays(data.downyMildewResult.days, data.irrigations) : undefined;
    },
    computation: (source, previous) => source ?? previous?.value ?? [],
  });

  protected readonly selectedAdvice = computed((): CropFieldAdvice[] => {
    const advice = this.store.selectedCropFieldAdvice();
    return advice ? [advice] : [];
  });

  protected readonly adviceForChart = linkedSignal<CropFieldAdvice | undefined, CropFieldAdvice | undefined>({
    source: () => {
      if (this.store.downyMildewDataIsLoading()) return undefined;
      const advice = this.store.selectedCropFieldAdvice();
      const data = this.store.selectedDownyMildewData();
      if (!advice || !data) {
        return advice;
      }

      const growthStageHistory = this.store.growthStageHistoryValue();
      const emergenceDateForAdvice =
        getAdviceVisibilityDate(growthStageHistory, data.cropFieldId) ?? advice.emergenceDate;

      return {
        ...advice,
        emergenceDate: emergenceDateForAdvice,
      };
    },
    computation: (source, previous) => source ?? previous?.value,
  });

  protected readonly actionDate = signal<Date | undefined>(undefined);

  @ViewChild('menuTrigger', { read: MatMenuTrigger }) private readonly menuTrigger!: MatMenuTrigger;
  @ViewChild('menuTrigger', { read: ElementRef }) private readonly menuTriggerEl!: ElementRef<HTMLElement>;

  protected readonly tooltipFormatter = (day: DiseaseChartDay, type: DownyMildewRisks): string => {
    const config: Record<DownyMildewRisks, { key: string; value: string | undefined }> = {
      combinedRisk: { key: 'downyMildew.riskChart.combinedRisk', value: day.combinedRiskLevel },
      sporulationRisk: { key: 'downyMildew.riskChart.sporulationRisk', value: day.sporulationRiskLevel },
      infectionRisk: { key: 'downyMildew.riskChart.infectionRisk', value: day.infectionRiskLevel },
    };
    const entry = config[type];
    if (!entry) {
      return '';
    }
    if (type !== 'sporulationRisk' && isAdviceHidden(day)) {
      return '';
    }
    const riskLevelKey = entry.value
      ? `downyMildew.riskChart.levels.${entry.value.toLowerCase()}`
      : 'downyMildew.riskChart.levels.unknown';
    const riskLevel = this.localeService.translate(riskLevelKey);
    return this.localeService.translate(entry.key) + ': ' + riskLevel;
  };

  private static readonly riskLevelKeys: Record<DownyMildewRisks, keyof DiseaseChartDay> = {
    combinedRisk: 'combinedRiskLevel',
    sporulationRisk: 'sporulationRiskLevel',
    infectionRisk: 'infectionRiskLevel',
  };

  protected getRiskCellStyle(day: DiseaseChartDay, riskType: DownyMildewRisks): { [key: string]: string } {
    const level = day[DownyMildewInsightPageComponent.riskLevelKeys[riskType]] as DownyMildewRiskLevel | undefined;
    return getRiskLevelStyling(level ?? 'Unknown');
  }

  protected form = new FormGroup({
    cropField: new FormControl(),
  });

  constructor() {
    this.initializeCropFieldSelection();
    this.initializeGrowthStageSelection();
    this.subscribeToCropFieldChange();
  }

  protected onSelectedDatesChanged(dates: Date[]): void {
    this.store.patchSelectedDates(dates);
  }

  protected onRecalculate(): void {
    const cropField = this.selectedCropField();
    if (cropField && !this.isReadOnly()) {
      this.ai.trackEvent(
        DownyMildewAIIdentifiers.insightsCalculateClicked,
        createDownyMildewTelemetryProperties('insightsCalculateClicked', {
          cropFieldId: cropField.cropFieldId,
        }),
      );
      this.store.recalculateDownyMildew([cropField.cropFieldId]);
    }
  }

  protected onPlantDateChanged(date: Date): void {
    const cropField = this.selectedCropField();
    if (!cropField || this.isReadOnly()) {
      return;
    }

    this.store.updatePlantDate({
      date,
      cropFieldId: cropField.cropFieldId,
      year: cropField.year,
    });
  }

  protected onThirdLeafStageDateChanged(date: Date): void {
    const cropField = this.selectedCropField();
    if (!cropField || this.isReadOnly()) {
      return;
    }

    this.store.registerGrowthStage({
      cropFieldIds: [cropField.cropFieldId],
      bbchCode: thirdLeafStageBbchCode,
      registrationDate: formatGrowthStageDate(date),
    });
  }

  protected onLeavesBentOverDateChanged(date: Date): void {
    const cropField = this.selectedCropField();
    if (!cropField || this.isReadOnly()) {
      return;
    }

    this.store.registerGrowthStage({
      cropFieldIds: [cropField.cropFieldId],
      bbchCode: leavesBentOverBbchCode,
      registrationDate: formatGrowthStageDate(date),
    });
  }

  protected onGrowthStageEditClicked(field: 'emergence' | 'harvest'): void {
    if (this.isReadOnly()) {
      return;
    }

    const cropField = this.selectedCropField();
    const hasEmergenceValue =
      !!cropField && !!getAdviceVisibilityDate(this.store.growthStageHistoryValue(), cropField.cropFieldId);
    const hasValue = field === 'harvest' ? !!cropField?.leavesBentOverDate : hasEmergenceValue;
    let preferredBbchCode: string | undefined;

    if (!hasValue) {
      preferredBbchCode = field === 'harvest' ? leavesBentOverBbchCode : thirdLeafStageBbchCode;
    }

    this.store.openGrowthStageDialog(this.selectedAdvice(), undefined, preferredBbchCode);
  }

  protected onActionTriggered({ event, date }: { event: MouseEvent; date: Date }): void {
    this.actionDate.set(date);
    const el = this.menuTriggerEl.nativeElement;
    el.style.left = `${event.clientX + 10}px`;
    el.style.top = `${event.clientY + 10}px`;
    this.menuTrigger.openMenu();
  }

  protected onActionMenuOpened(source: 'button' | 'chart' = 'button'): void {
    this.ai.trackEvent(
      DownyMildewAIIdentifiers.insightsActionMenuOpened,
      createDownyMildewTelemetryProperties('insightsActionMenuOpened', {
        actionType: source,
      }),
    );
  }

  protected onGraphSeriesToggled({ seriesName, visible }: { seriesName: string; visible: boolean }): void {
    this.ai.trackEvent(
      DownyMildewAIIdentifiers.insightsGraphSeriesToggled,
      createDownyMildewTelemetryProperties('insightsGraphSeriesToggled', {
        seriesName,
        isVisible: visible,
      }),
    );
  }

  private initializeCropFieldSelection(): void {
    effect(() => {
      const cropFields = this.cropFields();
      if (!cropFields?.length) {
        return;
      }

      const currentSelected = this.store.selectedCropFieldId();

      // If a crop field is already selected and still exists in the list,
      // just ensure the form control is in sync
      if (currentSelected && cropFields.some((cf) => cf.cropFieldId === currentSelected)) {
        const currentFormValue = this.form.controls.cropField.value;
        if (currentFormValue !== currentSelected) {
          untracked(() => {
            this.form.controls.cropField.setValue(currentSelected, { emitEvent: false });
            this.router.navigate([], {
              queryParams: { cropFieldId: currentSelected },
              queryParamsHandling: 'merge',
            });
          });
        }
        return;
      }

      // Fall back to query param or first available crop field
      const cropFieldQueryParam = this.route.snapshot.queryParamMap.get('cropFieldId');
      const queryParamCropFieldId = cropFieldQueryParam ? +cropFieldQueryParam : undefined;

      const targetId = queryParamCropFieldId
        ? (cropFields.find((cf) => cf.cropFieldId === queryParamCropFieldId)?.cropFieldId ?? cropFields[0]?.cropFieldId)
        : cropFields[0]?.cropFieldId;

      if (targetId) {
        untracked(() => {
          this.store.selectCropField(targetId);
          this.form.controls.cropField.setValue(targetId, { emitEvent: false });
          this.router.navigate([], {
            queryParams: { cropFieldId: targetId },
            queryParamsHandling: 'merge',
          });
        });
      }
    });
  }

  private subscribeToCropFieldChange(): void {
    this.form.controls.cropField.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((cropFieldId) => !!cropFieldId),
        distinctUntilChanged(),
      )
      .subscribe((cropFieldId) => {
        this.ai.trackEvent(
          DownyMildewAIIdentifiers.insightsCropFieldSelected,
          createDownyMildewTelemetryProperties('insightsCropFieldSelected', {
            cropFieldId: +cropFieldId,
          }),
        );
        this.store.selectCropField(+cropFieldId);
        this.router.navigate([], {
          queryParams: { cropFieldId },
          queryParamsHandling: 'merge',
        });
      });
  }

  private initializeGrowthStageSelection(): void {
    effect(() => {
      const data = this.store.selectedDownyMildewData();
      if (!data) {
        return;
      }

      this.store.setGrowthStageSelection([data.cropFieldId], data.year);
    });
  }
}

const thirdLeafStageBbchCode = '103';
const fourthLeafStageBbchCode = '104';
const leavesBentOverBbchCode = '805';
const leavesBentOverLegacyBbchCode = '408';

const getLatestGrowthStageDate = (
  registrations: GrowthStageRegistration[],
  cropFieldId: number,
  bbchCode: string,
): Date | undefined => {
  const matchingRegistrations = registrations
    .filter((registration) => registration.cropFieldId === cropFieldId && registration.bbchCode === bbchCode)
    .sort((left, right) => right.registrationDate.getTime() - left.registrationDate.getTime());

  const latestRegistration = matchingRegistrations[0];
  return latestRegistration ? new Date(latestRegistration.registrationDate) : undefined;
};

const getLatestGrowthStageDateByCodes = (
  registrations: GrowthStageRegistration[],
  cropFieldId: number,
  bbchCodes: string[],
): Date | undefined => {
  for (const bbchCode of bbchCodes) {
    const date = getLatestGrowthStageDate(registrations, cropFieldId, bbchCode);
    if (date) {
      return date;
    }
  }

  return undefined;
};

const getLatestGrowthStageDateForAnyCode = (
  registrations: GrowthStageRegistration[],
  cropFieldId: number,
  bbchCodes: string[],
): Date | undefined => {
  const latestRegistration = registrations
    .filter((registration) => registration.cropFieldId === cropFieldId && bbchCodes.includes(registration.bbchCode))
    .sort((left, right) => right.registrationDate.getTime() - left.registrationDate.getTime())[0];

  return latestRegistration ? new Date(latestRegistration.registrationDate) : undefined;
};

const getAdviceVisibilityDate = (registrations: GrowthStageRegistration[], cropFieldId: number): Date | undefined =>
  getLatestGrowthStageDateForAnyCode(registrations, cropFieldId, [thirdLeafStageBbchCode, fourthLeafStageBbchCode]);

const formatGrowthStageDate = (date: Date): string => {
  const nativeDate = new Date(+date);
  const day = nativeDate.getDate().toString().padStart(2, '0');
  const month = (nativeDate.getMonth() + 1).toString().padStart(2, '0');
  const year = nativeDate.getFullYear();
  return `${year}-${month}-${day}`;
};
